import {
  type FileRecordProps,
  type FileRecordType,
  type RawFileType,
} from '@packages/systems/core/records/FileRecord';
import {
  ASSET_ALT_TEXT_CHANGE_STARTED,
  ASSET_BULK_ASSETS_COMPRESSION_REQUESTED,
  ASSET_BULK_ASSETS_UNREGISTER_REQUESTED,
  ASSET_DETAILS_UPDATE_STARTED,
  ASSET_DETAILS_UPDATE_SUCCEEDED,
  ASSET_METADATA_UPDATED,
  ASSET_PROCESSING_TOGGLED,
  ASSET_TRANSCODE_PROGRESSED,
  ASSET_UNREGISTERED,
  ASSETS_ITEM_FETCH_REQUESTED,
  ASSETS_LIST_FIRST_FETCH_REQUESTED,
  ASSETS_LIST_NEXT_FETCH_REQUESTED,
  DYN_ASSET_PROCESSING_TOGGLED,
  LINK_SETTINGS_OVERLAY_TARGET_CREATE_REQUESTED,
  PAGE_ASSETS_LOADED,
} from '@packages/systems/designer/actions';
import type {LinkSettingsOverlayTargetCreateRequestedPayload} from '@packages/systems/designer/actions';
import {type AssetRequestParameters} from '@packages/systems/assetManager';
import {
  type ActionCreator,
  createAction,
} from '@packages/utilities/create-action';

type AssetUnregisteredPayload = {
  asset: FileRecordType;
};

export const overlayTargetCreateRequested: ActionCreator<
  typeof LINK_SETTINGS_OVERLAY_TARGET_CREATE_REQUESTED,
  LinkSettingsOverlayTargetCreateRequestedPayload
> = createAction(LINK_SETTINGS_OVERLAY_TARGET_CREATE_REQUESTED);

const assetUnregistered: ActionCreator<
  typeof ASSET_UNREGISTERED,
  AssetUnregisteredPayload
> = createAction(ASSET_UNREGISTERED);

type AssetTranscodeProgressedPayload = {
  id: string;
  progress: null | number;
};

const updateTranscodeProgress: ActionCreator<
  typeof ASSET_TRANSCODE_PROGRESSED,
  AssetTranscodeProgressedPayload
> = createAction(ASSET_TRANSCODE_PROGRESSED);

type AssetProcessingToggledPayload = {
  id: string;
  processing: boolean;
};

const toggleProcessing: ActionCreator<
  typeof ASSET_PROCESSING_TOGGLED,
  AssetProcessingToggledPayload
> = createAction(ASSET_PROCESSING_TOGGLED);

type DynAssetProcessingToggledPayload = {
  asset: FileRecordType;
  processing: boolean;
};

const dynAssetProcessingToggled: ActionCreator<
  typeof DYN_ASSET_PROCESSING_TOGGLED,
  DynAssetProcessingToggledPayload
> = createAction(DYN_ASSET_PROCESSING_TOGGLED);

type AssetMetadataUpdatedPayload = {
  assetsMetadata: Record<string, Partial<FileRecordProps>>;
};

const updateMetadata: ActionCreator<
  typeof ASSET_METADATA_UPDATED,
  AssetMetadataUpdatedPayload
> = createAction(ASSET_METADATA_UPDATED);

export type AssetActionRequestType = 'bulk' | 'individual';

export type deleteAssetsPayload = {
  fileIds: Array<string>;
  requestType: AssetActionRequestType;
};

const pageAssetsLoaded = (pageAssets: Array<RawFileType>) => ({
  type: PAGE_ASSETS_LOADED,
  payload: {pageAssets},
});

const assetsListFirstFetch = (queryParameters: AssetRequestParameters) => ({
  type: ASSETS_LIST_FIRST_FETCH_REQUESTED,
  payload: {queryParameters},
});

const assetItemFetch = (assetId: string) => ({
  type: ASSETS_ITEM_FETCH_REQUESTED,
  payload: {assetId},
});

const assetsListNextFetch = (queryParameters: AssetRequestParameters) => ({
  type: ASSETS_LIST_NEXT_FETCH_REQUESTED,
  payload: {queryParameters},
});

const bulkUnregisterAssets = (
  fileIds: Array<string>,
  requestType: AssetActionRequestType = 'bulk'
) => ({
  type: ASSET_BULK_ASSETS_UNREGISTER_REQUESTED,
  payload: {fileIds, requestType},
});

export type compressAssetsPayload = {
  fileIds: Array<string>;
  fileFormat: string;
  requestType: AssetActionRequestType;
};

const compressAssets = (
  fileIds: Array<string>,
  fileFormat: string = 'webp',
  requestType: AssetActionRequestType = 'bulk'
): {type: string; payload: compressAssetsPayload} => ({
  type: ASSET_BULK_ASSETS_COMPRESSION_REQUESTED,
  payload: {fileIds, fileFormat, requestType},
});

type UpdateAssetAltTextPayload = {
  assetId: string;
  alt: string | null;
  localeId?: string;
};

const updateAssetAltText: ActionCreator<
  typeof ASSET_ALT_TEXT_CHANGE_STARTED,
  UpdateAssetAltTextPayload
> = createAction(ASSET_ALT_TEXT_CHANGE_STARTED);

export type UpdateAssetDetailsPayload = {
  assetId: string;
  alt: string | null;
  name: string;
  localeId?: string;
};

export type UpdateAssetDetailsSucceededPayload = {
  response: {
    asset: RawFileType;
  };
};

const updateAssetDetails: ActionCreator<
  typeof ASSET_DETAILS_UPDATE_STARTED,
  UpdateAssetDetailsPayload
> = createAction(ASSET_DETAILS_UPDATE_STARTED);

const updateAssetDetailsSucceeded: ActionCreator<
  typeof ASSET_DETAILS_UPDATE_SUCCEEDED,
  UpdateAssetDetailsSucceededPayload
> = createAction(ASSET_DETAILS_UPDATE_SUCCEEDED);

export const AssetsActionCreators = {
  assetUnregistered,
  updateTranscodeProgress,
  toggleProcessing,
  dynAssetProcessingToggled,
  updateMetadata,
  pageAssetsLoaded,
  assetsListFirstFetch,
  assetItemFetch,
  assetsListNextFetch,
  bulkUnregisterAssets,
  compressAssets,
  updateAssetAltText,
  updateAssetDetails,
  updateAssetDetailsSucceeded,
} as const;

export * from './externalActionCreators';
